import { HSLA, HSVA, RGBA, TinyColor } from '@ctrl/tinycolor'
import debounce from 'lodash.debounce'
import React, { useCallback, useEffect, useMemo } from 'react'
import { ColorPickerColors } from 'src/editor/shared/ui-lib/color-picker/color-picker-colors'
import { ColorPickerFields } from 'src/editor/shared/ui-lib/color-picker/color-picker-fields'
import { ColorPickerHsl } from 'src/editor/shared/ui-lib/color-picker/color-picker-hsl'
import { ColorPickerSwitch } from 'src/editor/shared/ui-lib/color-picker/color-picker-switch'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { ReactColorChangeResult } from 'src/shared/types/color-typings'

export interface CommonColorPickerProps {
  initialColor?: string
  userColors: string[]
  isGradientAvailable?: boolean
  delay?: number
  isClearable?: boolean
  onChange: (color: string) => void
  onSavedColorsChange: (colors: string[]) => void
  onClose: () => void
}

interface ClearableColorPicker extends CommonColorPickerProps {
  isClearable?: true
  onChange: (color: string | undefined) => void
}

export enum ColorTypeOptionEnum {
  SOLID = 'solid',
  GRADIENT = 'gradient',
}

export const DEFAULT_COLOR = 'rgba(255, 255, 255, 0)'

export const CommonColorPicker = ({
  initialColor,
  onSavedColorsChange,
  userColors,
  isGradientAvailable = false,
  delay = 300,
  isClearable = false,
  onChange,
  onClose,
}: CommonColorPickerProps) => {
  const { t } = useTypedTranslation()
  const colorTypeOptions = useMemo(
    () => [
      {
        label: t('components.color_picker.type.solid'),
        value: ColorTypeOptionEnum.SOLID,
      },
      {
        label: t('components.color_picker.type.gradient'),
        value: ColorTypeOptionEnum.GRADIENT,
      },
    ],
    [t],
  )

  const [colorType, setColorType] = React.useState(colorTypeOptions[0])
  const [hex, setHex] = React.useState(new TinyColor(initialColor).toHex())
  const [rgba, setRgba] = React.useState<RGBA>(
    new TinyColor(initialColor).toRgb(),
  )
  const [hsl, setHsl] = React.useState<HSLA>(
    new TinyColor(initialColor).toHsl(),
  )
  const [hsv, setHsv] = React.useState<HSVA>(
    new TinyColor(initialColor).toHsv(),
  )

  // gradient states
  const [activeGradientIndex, setActiveGradientIndex] =
    React.useState<number>(0)
  const [angle, setAngle] = React.useState<number>(90)
  const [gradient, setGradient] = React.useState<[RGBA, RGBA]>([
    new TinyColor(initialColor).toRgb(),
    new TinyColor(initialColor).toRgb(),
  ])

  // user color states
  const [originalUserColor, setOriginalUserColor] = React.useState<string>()
  const [editedUserColorIndex, setEditedUserColorIndex] =
    React.useState<number>()

  const debouncedOnSavedColorsChange = useMemo(
    () => debounce(onSavedColorsChange, delay),
    [onSavedColorsChange, delay],
  )

  const handleUpdateUserColor = useCallback(
    (newColor: string) => {
      const newColors = userColors.map((color, index) =>
        index === editedUserColorIndex ? newColor : color,
      )
      debouncedOnSavedColorsChange(newColors)
    },
    [userColors, debouncedOnSavedColorsChange, editedUserColorIndex],
  )

  const saveColor = useCallback(
    (newColor: TinyColor, shouldUpdateUserColor = true) => {
      if (
        colorType.value === ColorTypeOptionEnum.GRADIENT &&
        activeGradientIndex !== undefined
      ) {
        setGradient(oldGradient => {
          const newGradient: [RGBA, RGBA] = oldGradient
          newGradient[activeGradientIndex] = new TinyColor(newColor).toRgb()
          return newGradient
        })
      }

      setHsv(newColor.toHsv())
      setHsl(newColor.toHsl())
      setHex(newColor.toHex())
      setRgba(newColor.toRgb())

      if (shouldUpdateUserColor && editedUserColorIndex !== undefined) {
        handleUpdateUserColor(newColor.toRgbString())
      }
    },
    [
      colorType.value,
      activeGradientIndex,
      editedUserColorIndex,
      handleUpdateUserColor,
    ],
  )

  const handleColorChange = (colorResult: ReactColorChangeResult) => {
    if (colorResult.source === 'hsv') {
      const newAlpha = colorResult.a === 0 ? 1 : colorResult.a
      const newColor = new TinyColor({
        h: colorResult.h,
        s: colorResult.s,
        v: colorResult.v,
        a: newAlpha,
      })

      saveColor(newColor)
    } else if (colorResult.source === 'hsl' || colorResult.source === 'rgb') {
      // "rgb" source is not a real rgb color
      // instead it provides an HSL color with changed alpha layer
      const newAlpha = colorResult.a === 0 ? 1 : colorResult.a
      const newColor = new TinyColor({
        h: colorResult.h,
        s: colorResult.s,
        l: colorResult.l,
        a: newAlpha,
      })

      saveColor(newColor)
    } else if (colorResult.source === 'hex') {
      const newColor = new TinyColor(colorResult.hex)
      setHex(colorResult.hex)
      // exclude 3-digit and 8-digit hex formats as they break with tiptap focus change
      if (
        newColor.isValid &&
        newColor.format === 'hex' &&
        colorResult.hex.length === 6
      ) {
        saveColor(newColor)
      }
    }
  }

  const handleOpacityChange = (opacity: number) => {
    const newColor = new TinyColor(hsl)
    newColor.setAlpha(opacity)

    saveColor(newColor)
  }

  const debouncedOnChange = useCallback(debounce(onChange, delay), [])
  useEffect(() => {
    if (new TinyColor(rgba).equals(initialColor)) {
      return
    }

    let rgbaString = ''
    if (colorType.value === ColorTypeOptionEnum.GRADIENT) {
      const gradientStart = new TinyColor(gradient[0]).toRgbString()
      const gradientEnd = new TinyColor(gradient[1]).toRgbString()
      rgbaString = `linear-gradient(${angle}deg, ${gradientStart} 0%, ${gradientEnd} 100%)`
    } else {
      rgbaString = new TinyColor(rgba).toRgbString()
    }

    debouncedOnChange(rgbaString)
  }, [rgba, angle, colorType.value, gradient, initialColor, debouncedOnChange])

  const setEditedUserColor = (index: number) => {
    if (index === editedUserColorIndex) {
      setEditedUserColorIndex(undefined)
      setOriginalUserColor(undefined)
      return
    }

    const newColor = new TinyColor(userColors[index])

    setEditedUserColorIndex(index)
    setOriginalUserColor(userColors[index])
    saveColor(newColor, false)
  }

  const handleRestoreOriginalUserColor = () => {
    const oldColor = new TinyColor(originalUserColor ?? DEFAULT_COLOR)

    onSavedColorsChange(
      userColors.map((color, index) =>
        index === editedUserColorIndex ? oldColor.toRgbString() : color,
      ),
    )
    saveColor(oldColor)
  }

  const handleClearColor = useCallback(() => {
    if (isClearable) {
      ;(onChange as ClearableColorPicker['onChange'])(undefined)
      onClose()
    }
  }, [isClearable, onChange, onClose])

  const calculatedOpacity = useMemo(() => Math.round(rgba.a * 100), [rgba.a])

  return (
    <div className="relative max-w-[280px] rounded-lg border border-btnSecondaryBorder bg-bgPrimaryBase p-4">
      <ColorPickerSwitch
        isGradientAvailable={isGradientAvailable}
        angle={angle}
        setAngle={setAngle}
        gradient={gradient}
        setGradient={setGradient}
        activeGradientIndex={activeGradientIndex}
        setActiveGradientIndex={setActiveGradientIndex}
        onClose={onClose}
        colorType={colorType}
        setColorType={setColorType}
        colorTypeOptions={colorTypeOptions}
      />
      <ColorPickerHsl
        rgba={rgba}
        hsv={hsv}
        hsl={hsl}
        handleColorChange={handleColorChange}
        handleOpacityChange={handleOpacityChange}
      />
      <ColorPickerFields
        hex={hex}
        opacity={calculatedOpacity}
        handleRestore={handleRestoreOriginalUserColor}
        handleOpacityChange={handleOpacityChange}
        handleColorChange={handleColorChange}
      />
      <ColorPickerColors
        handleEdit={setEditedUserColor}
        editedColorIndex={editedUserColorIndex}
        userColors={userColors}
        onSavedColorsChange={onSavedColorsChange}
        isClearable={isClearable}
        onClearColor={handleClearColor}
      />
    </div>
  )
}
